import { useCallback, useMemo, useRef, useState } from 'react';
import type { EventDropArg } from '@fullcalendar/core';
import type { EventDragStopArg, EventResizeDoneArg } from '@fullcalendar/interaction';

import { hasPointerCoords, pointerCoords } from '@/features/calendars/calendarDrag';
import { toast } from '@/lib/toast';
import {
  buildOccupied,
  canRestoreSingleBlock,
  getDroppedRange,
  intervalConflicts,
  isActiveBlock,
  newSplitGroupId,
  splitAroundOccupied,
  totalBlockDurationMs,
  type BlockSegment,
} from '@/features/calendars/scheduleConflict';
import type { BusyInterval, TimeBlock } from '@/features/calendars/types';
import type { Task } from '@/features/tasks/types';
import {
  useCreateTimeBlock,
  useDeleteTimeBlock,
  useUpdateTimeBlock,
} from '@/features/calendars/useTimeBlocks';
import { isLate, lateBy } from '@/features/calendars/backlogSort';

export type PendingPlacement = {
  kind: 'create' | 'move';
  taskId: number;
  taskTitle: string;
  blockId?: number;
  startsAt: Date;
  endsAt: Date;
};

function pointOverDailyBacklog(clientX: number, clientY: number): boolean {
  const stack = document.elementsFromPoint(clientX, clientY);
  return stack.some((el) => {
    if (!(el instanceof Element)) return false;
    if (el.closest('.fc-event-dragging, .fc-event-mirror, .fc-mirror')) return false;
    return Boolean(el.closest('[data-daily-backlog-drop]'));
  });
}

export function useDailyGridScheduling({
  blocks,
  busy,
  tasks,
  timezone,
}: {
  blocks: TimeBlock[];
  busy: BusyInterval[];
  tasks: Task[];
  timezone: string;
}) {
  const createBlock = useCreateTimeBlock();
  const updateBlock = useUpdateTimeBlock();
  const deleteBlock = useDeleteTimeBlock();

  const [dragConflicts, setDragConflicts] = useState(false);
  const [backlogDropActive, setBacklogDropActive] = useState(false);
  const [pendingPlacement, setPendingPlacement] = useState<PendingPlacement | null>(null);
  const dragMoveCleanupRef = useRef<(() => void) | null>(null);
  const dropHandledRef = useRef(false);
  const dragOriginRef = useRef<{
    id: string;
    start: Date;
    end: Date;
  } | null>(null);

  const blockById = useMemo(() => {
    const map = new Map<number, TimeBlock>();
    for (const b of blocks) map.set(b.id, b);
    return map;
  }, [blocks]);

  const taskById = useMemo(() => {
    const map = new Map<number, Task>();
    for (const t of tasks) map.set(t.id, t);
    return map;
  }, [tasks]);

  const occupiedForPlacement = useCallback(
    (exclude: { blockId?: number; taskId?: number } = {}) =>
      buildOccupied(busy, blocks, exclude),
    [busy, blocks],
  );

  const warnIfLate = useCallback(
    (taskId: number, endsAt: Date | string, fallbackTitle?: string) => {
      const task = taskById.get(taskId);
      const due = task?.due_at ?? null;
      if (!due || !isLate(due, endsAt)) return;
      const title = task?.title ?? fallbackTitle ?? 'Task';
      toast.show(
        `"${title}" is planned ${lateBy(due, endsAt, timezone)} past its due date`,
        { tone: 'warning' },
      );
    },
    [taskById, timezone],
  );

  /** Move a block; if it is a continuation segment and free time fits the full
   * estimate, restore a single block and delete siblings (same as Plan). */
  const applyBlockMove = useCallback(
    (
      blockId: number,
      taskId: number,
      taskTitle: string,
      start: Date,
      end: Date,
      onError?: () => void,
    ) => {
      const siblings = blocks.filter(
        (b) => b.task_id === taskId && b.id !== blockId && isActiveBlock(b.status),
      );
      const occupied = occupiedForPlacement({ blockId, taskId });

      if (siblings.length > 0) {
        const totalMs = totalBlockDurationMs(blocks, taskId);
        if (canRestoreSingleBlock(start, totalMs, occupied)) {
          updateBlock.mutate(
            {
              id: blockId,
              starts_at: start.toISOString(),
              ends_at: new Date(start.getTime() + totalMs).toISOString(),
              clear_split_group: true,
            },
            {
              onSuccess: () => {
                for (const s of siblings) deleteBlock.mutate(s.id);
                warnIfLate(taskId, new Date(start.getTime() + totalMs), taskTitle);
              },
              onError,
            },
          );
          return;
        }
      }

      updateBlock.mutate(
        {
          id: blockId,
          starts_at: start.toISOString(),
          ends_at: end.toISOString(),
          clear_split_group: true,
        },
        {
          onSuccess: () => warnIfLate(taskId, end, taskTitle),
          onError,
        },
      );
    },
    [blocks, deleteBlock, occupiedForPlacement, updateBlock, warnIfLate],
  );

  const placeSegments = useCallback(
    async (
      taskId: number,
      segments: BlockSegment[],
      groupId: string,
      replaceBlockId?: number,
    ) => {
      const first = segments[0];
      if (!first) return;
      const rest = segments.slice(1);
      if (replaceBlockId != null) {
        await updateBlock.mutateAsync({
          id: replaceBlockId,
          starts_at: first.startsAt.toISOString(),
          ends_at: first.endsAt.toISOString(),
          is_continuation: first.isContinuation,
          split_group_id: groupId,
        });
      } else {
        await createBlock.mutateAsync({
          task_id: taskId,
          starts_at: first.startsAt.toISOString(),
          ends_at: first.endsAt.toISOString(),
          status: 'confirmed',
          origin: 'manual_drag',
          is_continuation: first.isContinuation,
          split_group_id: groupId,
        });
      }
      for (const seg of rest) {
        await createBlock.mutateAsync({
          task_id: taskId,
          starts_at: seg.startsAt.toISOString(),
          ends_at: seg.endsAt.toISOString(),
          status: 'confirmed',
          origin: 'manual_drag',
          is_continuation: seg.isContinuation,
          split_group_id: groupId,
        });
      }
    },
    [createBlock, updateBlock],
  );

  const commitOverlap = useCallback(
    (pending: PendingPlacement) => {
      if (pending.kind === 'create') {
        createBlock.mutate(
          {
            task_id: pending.taskId,
            starts_at: pending.startsAt.toISOString(),
            ends_at: pending.endsAt.toISOString(),
            status: 'confirmed',
            origin: 'manual_drag',
          },
          {
            onSuccess: () => {
              warnIfLate(pending.taskId, pending.endsAt);
              setPendingPlacement(null);
            },
          },
        );
        return;
      }
      updateBlock.mutate(
        {
          id: pending.blockId!,
          starts_at: pending.startsAt.toISOString(),
          ends_at: pending.endsAt.toISOString(),
          clear_split_group: true,
        },
        {
          onSuccess: () => {
            warnIfLate(pending.taskId, pending.endsAt, pending.taskTitle);
            setPendingPlacement(null);
          },
        },
      );
    },
    [createBlock, updateBlock, warnIfLate],
  );

  const commitSplit = useCallback(
    async (pending: PendingPlacement) => {
      const occupied = occupiedForPlacement(
        pending.kind === 'move'
          ? { blockId: pending.blockId, taskId: pending.taskId }
          : {},
      );
      const segments = splitAroundOccupied(pending.startsAt, pending.endsAt, occupied);
      if (!segments) {
        commitOverlap(pending);
        return;
      }
      const existing =
        pending.blockId != null ? blockById.get(pending.blockId) : undefined;
      const groupId = existing?.split_group_id ?? newSplitGroupId();
      try {
        await placeSegments(pending.taskId, segments, groupId, pending.blockId);
        warnIfLate(pending.taskId, segments.at(-1)!.endsAt, pending.taskTitle);
        setPendingPlacement(null);
      } catch {
        toast.error('Could not split block');
      }
    },
    [blockById, commitOverlap, occupiedForPlacement, placeSegments, warnIfLate],
  );

  const queuePlacement = useCallback(
    (pending: PendingPlacement) => {
      const occupied = occupiedForPlacement(
        pending.kind === 'move'
          ? { blockId: pending.blockId, taskId: pending.taskId }
          : {},
      );
      if (!intervalConflicts(pending.startsAt, pending.endsAt, occupied)) {
        commitOverlap(pending);
        return;
      }
      setPendingPlacement(pending);
    },
    [commitOverlap, occupiedForPlacement],
  );

  const placeBlock = useCallback(
    (taskId: number, startsAt: Date, endsAt: Date) => {
      const task = taskById.get(taskId);
      queuePlacement({
        kind: 'create',
        taskId,
        taskTitle: task?.title ?? 'Task',
        startsAt,
        endsAt,
      });
    },
    [queuePlacement, taskById],
  );

  const scheduleTask = useCallback(
    (task: Task, startsAt: string, endsAt: string) => {
      placeBlock(task.id, new Date(startsAt), new Date(endsAt));
    },
    [placeBlock],
  );


  const returnBlockToBacklog = useCallback(
    (block: TimeBlock) => {
      const snapshot = { ...block };
      deleteBlock.mutate(block.id, {
        onSuccess: () => {
          toast.undoable(`Returned "${block.task_title}" to backlog`, () => {
            createBlock.mutate({
              task_id: snapshot.task_id,
              starts_at: snapshot.starts_at,
              ends_at: snapshot.ends_at,
              status: snapshot.status === 'proposed' ? 'proposed' : 'confirmed',
              origin: snapshot.origin,
            });
          });
        },
      });
    },
    [createBlock, deleteBlock],
  );

  const onDrop = useCallback(
    (info: { date: Date; draggedEl: HTMLElement; allDay: boolean }) => {
      if (info.allDay) return;
      const taskId = Number(info.draggedEl.dataset.taskId);
      if (!Number.isFinite(taskId)) return;
      const minutes = Number(info.draggedEl.dataset.minutes) || 30;
      const start = info.date;
      const end = new Date(start.getTime() + minutes * 60_000);
      placeBlock(taskId, start, end);
      setDragConflicts(false);
    },
    [placeBlock],
  );

  const commitBlockMove = useCallback(
    (arg: EventDropArg) => {
      dropHandledRef.current = true;
      const id = arg.event.id;
      if (!id.startsWith('block-')) {
        arg.revert();
        return;
      }
      const { x, y } = pointerCoords(arg.jsEvent);
      if (hasPointerCoords(arg.jsEvent) && pointOverDailyBacklog(x, y)) {
        arg.revert();
        return;
      }
      const blockId = Number(id.slice(6));
      const range = getDroppedRange(arg);
      if (!range) {
        arg.revert();
        return;
      }
      const { start, end } = range;
      setDragConflicts(false);
      const block = blockById.get(blockId);
      if (!block) {
        arg.revert();
        return;
      }
      if (
        intervalConflicts(
          start,
          end,
          occupiedForPlacement({ blockId, taskId: block.task_id }),
        )
      ) {
        arg.revert();
        setPendingPlacement({
          kind: 'move',
          taskId: block.task_id,
          taskTitle: block.task_title,
          blockId,
          startsAt: start,
          endsAt: end,
        });
        return;
      }
      applyBlockMove(blockId, block.task_id, block.task_title, start, end, () =>
        arg.revert(),
      );
    },
    [applyBlockMove, blockById, occupiedForPlacement],
  );

  const onEventDrop = useCallback(
    (arg: EventDropArg) => {
      commitBlockMove(arg);
    },
    [commitBlockMove],
  );

  const onEventResize = useCallback(
    (arg: EventResizeDoneArg) => {
      const id = arg.event.id;
      if (!id.startsWith('block-')) {
        arg.revert();
        return;
      }
      const blockId = Number(id.slice(6));
      const range = getDroppedRange(arg);
      if (!range) {
        arg.revert();
        return;
      }
      const { start, end } = range;
      const block = blockById.get(blockId);
      if (!block) {
        arg.revert();
        return;
      }
      if (
        intervalConflicts(
          start,
          end,
          occupiedForPlacement({ blockId, taskId: block.task_id }),
        )
      ) {
        arg.revert();
        setPendingPlacement({
          kind: 'move',
          taskId: block.task_id,
          taskTitle: block.task_title,
          blockId,
          startsAt: start,
          endsAt: end,
        });
        return;
      }
      applyBlockMove(blockId, block.task_id, block.task_title, start, end, () =>
        arg.revert(),
      );
    },
    [applyBlockMove, blockById, occupiedForPlacement],
  );

  const onEventDrag = useCallback(
    (arg: { event: { id: string; start: Date | null; end: Date | null } }) => {
      const start = arg.event.start;
      const end = arg.event.end;
      if (!start || !end) {
        setDragConflicts(false);
        return;
      }
      if (!arg.event.id.startsWith('block-')) {
        setDragConflicts(false);
        return;
      }
      const blockId = Number(arg.event.id.slice(6));
      const block = blockById.get(blockId);
      const exclude = block ? { blockId, taskId: block.task_id } : { blockId };
      setDragConflicts(intervalConflicts(start, end, occupiedForPlacement(exclude)));
    },
    [blockById, occupiedForPlacement],
  );

  const onEventDragStart = useCallback(
    (arg: { event: { id: string; start: Date | null; end: Date | null } }) => {
      dropHandledRef.current = false;
      if (arg.event.start && arg.event.end) {
        dragOriginRef.current = {
          id: arg.event.id,
          start: arg.event.start,
          end: arg.event.end,
        };
      } else {
        dragOriginRef.current = null;
      }

      dragMoveCleanupRef.current?.();
      const onMove = (e: PointerEvent) => {
        setBacklogDropActive(pointOverDailyBacklog(e.clientX, e.clientY));
      };
      document.addEventListener('pointermove', onMove);
      dragMoveCleanupRef.current = () => {
        document.removeEventListener('pointermove', onMove);
        setBacklogDropActive(false);
        dragMoveCleanupRef.current = null;
      };
    },
    [],
  );

  const onEventDragStop = useCallback(
    (info: EventDragStopArg) => {
      dragMoveCleanupRef.current?.();
      setDragConflicts(false);
      setBacklogDropActive(false);

      const origin = dragOriginRef.current;
      dragOriginRef.current = null;

      // Touch drags sometimes skip eventDrop; persist here when the slot changed.
      if (
        !dropHandledRef.current &&
        origin &&
        info.event.id === origin.id &&
        info.event.id.startsWith('block-') &&
        info.event.start &&
        info.event.start.getTime() !== origin.start.getTime()
      ) {
        commitBlockMove({
          event: info.event,
          oldEvent: { ...info.event, start: origin.start, end: origin.end },
          revert: () => {
            info.event.setStart(origin.start);
            info.event.setEnd(origin.end);
          },
          jsEvent: info.jsEvent,
        } as EventDropArg);
      }
      dropHandledRef.current = false;

      const { x, y } = pointerCoords(info.jsEvent);
      if (!hasPointerCoords(info.jsEvent) || !pointOverDailyBacklog(x, y)) return;
      if (!info.event.id.startsWith('block-')) return;

      const block = blockById.get(Number(info.event.id.slice(6)));
      if (block) returnBlockToBacklog(block);
    },
    [blockById, commitBlockMove, returnBlockToBacklog],
  );

  const pendingCanSplit = useMemo(() => {
    if (!pendingPlacement) return false;
    const occupied = occupiedForPlacement(
      pendingPlacement.kind === 'move'
        ? { blockId: pendingPlacement.blockId, taskId: pendingPlacement.taskId }
        : {},
    );
    const segments = splitAroundOccupied(
      pendingPlacement.startsAt,
      pendingPlacement.endsAt,
      occupied,
    );
    return segments != null && segments.length > 1;
  }, [occupiedForPlacement, pendingPlacement]);

  return {
    dragConflicts,
    backlogDropActive,
    pendingPlacement,
    pendingCanSplit,
    setPendingPlacement,
    commitOverlap,
    commitSplit,
    scheduleTask,
    placeBlock,
    returnBlockToBacklog,
    onDrop,
    onEventDrop,
    onEventResize,
    onEventDrag,
    onEventDragStart,
    onEventDragStop,
    isScheduling: createBlock.isPending || updateBlock.isPending,
  };
}
